import stories from "./stories.json" with { type: "json" };

export const WATCH_FUNNEL_ID = "story-watch";
export const SLIM_FUNNEL_ID = "story-slim";

export const isWatchPath = (pathname: string): boolean =>
  /^\/watch(?:\/|$)/.test(pathname);

export const isSlimPath = (pathname: string): boolean =>
  /^\/slim(?:\/|$)/.test(pathname);

export function resolveSlimStory(pathname: string): string | null {
  const slug = /^\/slim\/([a-z0-9-]+)\/?$/.exec(pathname)?.[1];
  return slug && WATCH_STORIES.has(slug) ? slug : null;
}

export function resolveWatchStory(pathname: string): string | null {
  const slug = /^\/watch\/([a-z0-9-]+)\/?$/.exec(pathname)?.[1];
  return slug && WATCH_STORIES.has(slug) ? slug : null;
}

export type WatchVideoSource = { src: string; type: string };

/** Silent 540 × 960 / 30 fps exports. See docs/watch-video-exports.md. */
export function watchBackgroundSources(
  slug: string,
  version = "v1",
): readonly WatchVideoSource[] {
  const base = `/watch-video/${slug}/${version}`;
  return [
    { src: `${base}/av1-crf48.mp4`, type: 'video/mp4; codecs="av01.0.04M.08"' },
    { src: `${base}/hevc-crf31.mp4`, type: 'video/mp4; codecs="hvc1.1.6.L90.90"' },
    { src: `${base}/h264-crf30.mp4`, type: 'video/mp4; codecs="avc1.64001f"' },
  ];
}

export const WATCH_STORIES = new Map(
  stories.map((story) => [
    story.slug,
    {
      ...story,
      poster: `/watch-video/${story.slug}/${story.videoVersion}/poster.jpg`,
      backgroundVideo: watchBackgroundSources(story.slug, story.videoVersion),
    },
  ]),
);
export type WatchStory = NonNullable<ReturnType<typeof WATCH_STORIES.get>>;

export type WatchStage = "preview" | "quiz" | "plans" | "checkout" | "checkout_complete";

export function resolveWatchStage(urlLike: string): WatchStage {
  const url = new URL(urlLike);
  if (
    url.hash === "#checkout_complete" ||
    url.searchParams.get("step") === "checkout_complete" ||
    url.searchParams.has("subscription_id") ||
    url.searchParams.has("session_id")
  )
    return "checkout_complete";
  if (isSlimPath(url.pathname) && /^#quiz(?:\/|$)/.test(url.hash)) return "quiz";
  if (url.hash === "#checkout") return "checkout";
  return url.hash === "#plans" ? "plans" : "preview";
}

export function watchAttribution(urlLike: string) {
  const url = new URL(urlLike);
  const params = url.searchParams;
  const slim = isSlimPath(url.pathname);
  const storySlug = (slim ? resolveSlimStory(url.pathname) : resolveWatchStory(url.pathname)) ?? "goliath";
  const id = (value: string | null) =>
    value && /^[A-Za-z0-9._-]{1,128}$/.test(value) ? value : undefined;
  return {
    funnelId: slim ? SLIM_FUNNEL_ID : WATCH_FUNNEL_ID,
    storySlug,
    creativeId: id(params.get("creative_id") ?? params.get("utm_content")),
    experimentId: id(params.get("experiment_id")) ?? `${storySlug}-${slim ? "slim" : "preview"}-v1`,
  };
}
